import { z } from 'zod';
import { fullNameSchema } from './schemas.js';
/**
 * Compliance validation contracts (Phase 1 — KYC/AML "due diligence" engine).
 *
 * The compliance process runs a set of independent, modular *validations*
 * against a subject (a natural person or legal entity) during intake and on
 * periodic re-review. Each validation is a self-contained workflow with a
 * common contract, invoked through the API one step/document at a time. These
 * types are the shared language between the API (authoritative engine) and the
 * web client (UX). The engine implementation lives in
 * `packages/api/src/validations/`.
 */
/** Every validation the compliance process can run. Keep in sync with the engine registry. */
export const VALIDATION_KEYS = [
    'identity', // SVI — identity vs. Tribunal Electoral / ID-doc OCR
    'credit', // APC — credit bureau report & score
    'sanctions', // sanctions / watchlist screening (OFAC, UN, EU, local)
    'pep', // politically-exposed-person screening
    'adverse_media', // adverse / negative media screening
    'address', // domicile verification (proof-of-address OCR vs. declared)
    'registry', // public registry (Registro Público) — legal persons
    'document_completeness', // required documents present per persona
    'document_expiry', // document validity windows
    'document_ocr', // document OCR extraction + cross-check
    'ubo', // beneficial-ownership resolution + recursive KYC
    'risk_scoring', // weighted risk score (aggregates the above)
];
/**
 * Outcome of a single validation run.
 * - `ok`      passed, no issue
 * - `hit`     a positive match / blocking finding (e.g. on a sanctions list)
 * - `review`  inconclusive — needs a human decision
 * - `pending` not yet run / awaiting input or an upstream provider
 * - `error`   the validation could not complete (provider/system failure)
 */
export const VALIDATION_STATUSES = ['ok', 'hit', 'review', 'pending', 'error'];
/** The kind of subject a validation runs against. */
export const SUBJECT_TYPES = ['natural', 'legal'];
export const VALIDATION_CATALOG = {
    identity: { key: 'identity', title: 'Identidad (SVI)', group: 'identidad', appliesTo: ['natural', 'legal'], external: true },
    credit: { key: 'credit', title: 'Crédito (APC)', group: 'identidad', appliesTo: ['natural', 'legal'], external: true },
    sanctions: { key: 'sanctions', title: 'Listas de sanciones', group: 'screening', appliesTo: ['natural', 'legal'], external: true },
    pep: { key: 'pep', title: 'Persona Expuesta Políticamente', group: 'screening', appliesTo: ['natural', 'legal'], external: true },
    adverse_media: { key: 'adverse_media', title: 'Medios adversos', group: 'screening', appliesTo: ['natural', 'legal'], external: true },
    address: { key: 'address', title: 'Domicilio', group: 'documentos', appliesTo: ['natural', 'legal'], external: true },
    registry: { key: 'registry', title: 'Registro Público', group: 'documentos', appliesTo: ['legal'], external: true },
    document_completeness: { key: 'document_completeness', title: 'Completitud documental', group: 'documentos', appliesTo: ['natural', 'legal'], external: false },
    document_expiry: { key: 'document_expiry', title: 'Vigencia documental', group: 'documentos', appliesTo: ['natural', 'legal'], external: false },
    document_ocr: { key: 'document_ocr', title: 'Extracción documental (OCR)', group: 'documentos', appliesTo: ['natural', 'legal'], external: true },
    ubo: { key: 'ubo', title: 'Beneficiario final (UBO)', group: 'riesgo', appliesTo: ['legal'], external: false },
    risk_scoring: { key: 'risk_scoring', title: 'Puntaje de riesgo', group: 'riesgo', appliesTo: ['natural', 'legal'], external: false },
};
export const validationKeySchema = z.enum(VALIDATION_KEYS);
/** Request to run one validation against a subject. */
export const runValidationSchema = z.object({
    subjectType: z.enum(SUBJECT_TYPES),
    subjectId: z.string().trim().min(1).max(120),
    fullName: fullNameSchema,
    /** ISO 3166-1 alpha-2 country code (uppercased), optional. */
    country: z
        .string()
        .trim()
        .length(2)
        .transform((s) => s.toUpperCase())
        .optional(),
    /** Cédula / RUC / passport number, optional. */
    idNumber: z.string().trim().max(64).optional(),
});
export const listValidationRunsQuerySchema = z.object({
    subjectId: z.string().trim().min(1).max(120),
});
//# sourceMappingURL=validations.js.map